import { ThunkDispatch } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementsById } from "core-integration/src/store/elements/types";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getGroupedFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import {
  IFixtureStat,
  IFixtureStats,
} from "core-integration/src/store/fixtures/types";
import { format } from "date-fns";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import Box from "./Box";

import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dateLocales } from "../i18n";
import { getStatDetails } from "../utils/stats";
import Button from "./Button";
import Fixture from "./Fixture";
import { Pager, PagerButton, PagerButtonNext } from "./Pager";
import { ArrowLeft, ArrowRight } from "./icons/Arrows";

const FixtureSection = styled.section`
  margin-top: ${({ theme }) => theme.space[4]};
  padding: ${({ theme }) =>
    `${theme.space[4]} ${theme.space[2]} ${theme.space[2]}`};
  border-radius: ${({ theme }) => theme.space[1]};
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const FixtureGroups = styled.div`
  margin-left: 0.4rem;
  margin-right: 0.4rem;
  margin-bottom: ${({ theme }) => theme.space[2]};
`;

const Deadline = styled.h2`
  margin: ${({ theme }) => theme.space[1]};
  font-size: ${({ theme }) => theme.fontSizes[3]};
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.base};
  font-size: ${({ theme }) => theme.fontSizes[3]};
  font-weight: 500;
  text-transform: uppercase;
`;

const Heading = styled.h3`
  margin: ${({ theme }) =>
    `${theme.space[2]} ${theme.space[2]} ${theme.space[3]}`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: ${({ theme }) => theme.fontSizes[7]};
  font-weight: normal;
  color: ${({ theme }) => theme.colors.primary};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin: ${({ theme }) => theme.space[2]};
    font-size: ${({ theme }) => theme.fontSizes[8]};
  }
`;

const Note = styled.p`
  color: #6c6c6c;
  text-align: center;
`;

const FixtureDay = styled.h4`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  text-align: center;
`;

const FixtureTime = styled.time`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const FixtureList = styled.ul`
  margin: 0;
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureItem = styled.li`
  margin-bottom: ${({ theme }) => theme.space[2]};
  padding: 0;
  font-size: 1.1rem;
  list-style: none;
`;

const FixtureButton = styled.button<IStyledFixtureStatsProps>`
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  text-align: start;
  cursor: pointer;

  ${(props) =>
    props.isOpen &&
    css`
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    `}
`;

const FixtureStatsWrap = styled.div<IStyledFixtureStatsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  border-end-start-radius: ${({ theme }) => theme.radii[2]};
  border-end-end-radius: ${({ theme }) => theme.radii[2]};
  padding: ${({ theme }) => `${theme.space[2]} 5%`};
  background-color: ${({ theme }) => theme.colors.secondaryTwo};

  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    padding-inline-end: 15%;
    padding-inline-start: 15%;
  }
`;

const FixtureStatsOuterList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FixtureStat = styled.li`
  margin-bottom: ${({ theme }) => theme.space[4]};
  border-end-start-radius: ${({ theme }) => theme.radii[1]};
  border-end-end-radius: ${({ theme }) => theme.radii[1]};
  background-color: white;
`;

const FixtureStatHeading = styled.h5`
  margin: 0;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[2]}`};
  background-color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes[0]};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
`;

const FixtureStatBody = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.space[2]};
  padding-bottom: ${({ theme }) => theme.space[2]};
  border-end-start-radius: ${({ theme }) => theme.radii[1]};
  border-end-end-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const FixtureStatList = styled.ul`
  flex: 1;
  padding-inline-end: ${({ theme }) => theme.space[2]};
  padding-inline-start: ${({ theme }) => theme.space[2]};

  &:first-child {
    border-inline-end: 1px dotted ${({ theme }) => theme.colors.secondaryTwo};
    text-align: end;
  }
`;

const FixtureStatItem = styled.li`
  padding-top: ${({ theme }) => theme.space[1]};
  padding-bottom: ${({ theme }) => theme.space[1]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  list-style: none;

  &:last-child {
    border-bottom: 0;
  }
`;

const ElementButton = styled.button`
  background-color: transparent;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${({ theme }) => theme.fontSizes[2]};

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

interface IFixtureStatsForTeamProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStat[];
}

const FixtureStatsForTeam: React.FC<IFixtureStatsForTeamProps> = ({
  elementsById,
  showElementDialog,
  stats,
}) => (
  <FixtureStatList>
    {stats
      .filter((fs) => elementsById[fs.element])
      .map((fs) => (
        <FixtureStatItem key={fs.element}>
          <ElementButton onClick={(_: any) => showElementDialog(fs.element)}>
            {elementsById[fs.element].web_name}
          </ElementButton>{" "}
          ({fs.value})
        </FixtureStatItem>
      ))}
  </FixtureStatList>
);

interface IFixtureStatsProps {
  elementsById: IElementsById;
  showElementDialog: (elementId: number) => void;
  stats: IFixtureStats[];
  statsByIdentifier: Record<string, IElementStat>;
}

interface IStyledFixtureStatsProps {
  isOpen?: boolean;
}

type FixtureStatProps = IFixtureStatsProps & IStyledFixtureStatsProps;

const FixtureStats: React.FC<FixtureStatProps> = ({
  elementsById,
  isOpen = false,
  showElementDialog,
  stats,
}) => {
  const { t } = useTranslation();
  // Our render stats should contain only stats with data and a maximum
  // of 5 per team of BPS
  const renderStats = stats
    .filter((stat) => stat.h.length || stat.a.length)
    .map((stat) =>
      stat.identifier === "bps"
        ? {
            identifier: stat.identifier,
            h: stat.h.slice(0, 5),
            a: stat.a.slice(0, 5),
          }
        : stat,
    );
  return (
    <FixtureStatsWrap isOpen={isOpen}>
      <FixtureStatsOuterList>
        {renderStats.map((stat) => {
          const statDetails = getStatDetails(stat.identifier, t);
          if (!statDetails) {
            console.warn(`Unknown stat identifier: ${stat.identifier}`);
            return null;
          }
          return (
            <FixtureStat key={stat.identifier}>
              <FixtureStatHeading>{statDetails.label}</FixtureStatHeading>
              <FixtureStatBody>
                <FixtureStatsForTeam
                  stats={stat.h}
                  elementsById={elementsById}
                  showElementDialog={showElementDialog}
                />
                <FixtureStatsForTeam
                  stats={stat.a}
                  elementsById={elementsById}
                  showElementDialog={showElementDialog}
                />
              </FixtureStatBody>
            </FixtureStat>
          );
        })}
      </FixtureStatsOuterList>
    </FixtureStatsWrap>
  );
};

interface IOwnProps {
  eventId?: number;
  useLinks?: boolean;
}

type Props = IOwnProps & WithTranslation;
declare global {
  interface Window {
    postscribe: any;
  }
}

const Fixtures: React.FC<Props> = ({
  eventId: initialEventId,
  i18n,
  t,
  useLinks,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const currentEvent = useSelector(getCurrentEvent);
  const elementsById = useSelector(getElementsById);
  const eventsById = useSelector(getEventsById);
  const groupedFixturesByEvent = useSelector(getGroupedFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);
  const statsByIdentifier = useSelector(getElementStatsByIdentifier);

  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const [eventId, setEventId] = useState(() => {
    if (initialEventId) {
      return initialEventId;
    } else if (nextEvent) {
      return nextEvent.id;
    } else if (currentEvent) {
      return currentEvent.id;
    }
    return 0;
  });

  useEffect(() => {
    dispatch(fetchFixtures(eventId));
  }, [dispatch, eventId]);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const changeEvent = (newEventId: number) => {
    setEventId(newEventId);
    dispatch(fetchFixtures(newEventId));
  };

  const toggle = (fixtureId: number) => {
    setIsOpen((prevState) => ({
      ...prevState,
      [fixtureId]: !prevState[fixtureId],
    }));
  };

  const event = eventsById[eventId];
  if (!event) {
    return null;
  }

  const eventFixtureGroups = groupedFixturesByEvent[eventId] || [];
  return (
    <FixtureSection>
      <header>
        <Deadline>
          {event.name} -{" "}
          <time dateTime={formatRawAsISO(event.deadline_time)}>
            {formatRawAsLocalI18n(
              event.deadline_time,
              dateLocales[i18n.language],
            )}
          </time>
        </Deadline>
        <Heading>
          <span>{t("fixtures.title", "Fixtures")}</span>
        </Heading>
        <div>
          <Pager>
            {eventsById[eventId - 1] && (
              <PagerButton>
                {useLinks ? (
                  <Button
                    width={1}
                    to={`/fixtures/${eventId - 1}`}
                    onClick={() => changeEvent(eventId - 1)}
                    variant="secondary"
                    justifyContent="space-between"
                    startIcon={<ArrowLeft color="white" />}
                  >
                    {t("fixtures.previous", "Previous")}
                  </Button>
                ) : (
                  <Button
                    width={1}
                    onClick={() => changeEvent(eventId - 1)}
                    variant="secondary"
                    justifyContent="space-between"
                    startIcon={<ArrowLeft color="white" />}
                  >
                    {t("fixtures.previous", "Previous")}
                  </Button>
                )}
              </PagerButton>
            )}
            {eventsById[eventId + 1] && (
              <PagerButtonNext>
                {useLinks ? (
                  <Button
                    width={1}
                    to={`/fixtures/${eventId + 1}`}
                    onClick={() => changeEvent(eventId + 1)}
                    variant="secondary"
                    justifyContent="space-between"
                    endIcon={<ArrowRight color="white" />}
                  >
                    {t("fixtures.next", "Next")}
                  </Button>
                ) : (
                  <Button
                    width={1}
                    onClick={() => changeEvent(eventId + 1)}
                    disabled={!eventsById[eventId + 1]}
                    variant="secondary"
                    justifyContent="space-between"
                    endIcon={<ArrowRight color="white" />}
                  >
                    {t("fixtures.next", "Next")}
                  </Button>
                )}
              </PagerButtonNext>
            )}
          </Pager>
        </div>
      </header>
      <Box p={2}>
        <Note>
          <Trans i18nKey="fixtures.localTimes">
            All times are shown in your <strong>local time</strong>
          </Trans>
        </Note>
      </Box>
      {eventFixtureGroups.map((group) => (
        <FixtureGroups key={group.date.toISOString()}>
          <FixtureDay>
            <FixtureTime dateTime={group.date.toISOString()}>
              {format(group.date, "EEEE d MMMM yyyy")}
            </FixtureTime>
          </FixtureDay>
          <FixtureList>
            {group.fixtures.map((f) => (
              <React.Fragment key={f.id}>
                {f.started && f.team_h_score !== null ? (
                  <FixtureItem>
                    <FixtureButton
                      onClick={() => toggle(f.id)}
                      isOpen={isOpen[f.id]}
                    >
                      <Fixture isOpen={isOpen[f.id]} fixture={f} />
                    </FixtureButton>
                    <FixtureStats
                      stats={f.stats}
                      elementsById={elementsById}
                      showElementDialog={showElementDialog}
                      statsByIdentifier={statsByIdentifier}
                      isOpen={isOpen[f.id]}
                    />
                  </FixtureItem>
                ) : (
                  <FixtureItem>
                    <Fixture fixture={f} />
                  </FixtureItem>
                )}
              </React.Fragment>
            ))}
          </FixtureList>
        </FixtureGroups>
      ))}
    </FixtureSection>
  );
};

export default withTranslation()(Fixtures);
